import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { FacebookDisplayCreative, ICallToActionType } from '@feathr/blackbox';
import {
  CallToActionTypes,
  getCallToActionsByObjective,
  OBJECTIVE_TO_CTA_CATEGORY,
} from '@feathr/blackbox';
import { Input, Textarea, UniversalSelect } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import { useOgTags } from './MetaFields.hooks';

interface IMetaFieldsProps {
  creative: FacebookDisplayCreative;
  /** Campaign ID, used to filter CTA options by campaign objective. */
  campaignId?: string;
}

/**
 * Returns the CTA options filtered by campaign objective.
 * Accepts either a Facebook objective type (e.g. OUTCOME_AWARENESS) which is mapped
 * to a category, or a direct CTA category key (e.g. reach, high_demand, conversions)
 * used by OmniCampaigns.
 * Falls back to all CTA options when no objective is set or no mapping exists.
 */
export function getCtaOptions(campaignObjective: string | undefined): ICallToActionType[] {
  if (!campaignObjective) {
    return CallToActionTypes;
  }

  const category = OBJECTIVE_TO_CTA_CATEGORY[campaignObjective] ?? campaignObjective;
  return getCallToActionsByObjective(category);
}

function MetaFields({ creative, campaignId }: Readonly<IMetaFieldsProps>): JSX.Element {
  const { t } = useTranslation();
  const { Campaigns } = useStore();

  const campaign = campaignId ? Campaigns.get(campaignId) : undefined;
  const objectiveType = campaign?.get('objective_type') ?? campaign?.get('objective');
  const ctaOptions = getCtaOptions(objectiveType);

  const destinationUrl = creative.get('destination_url');
  const { ogHeadline, ogDescription, ogDisplayLink } = useOgTags(destinationUrl);

  /*
   * Auto-populate empty creative fields from OG tag data when a new URL is fetched.
   * Uses a ref to track the last URL we applied OG data for, so we only apply once
   * per URL change and never overwrite user-entered values.
   */
  const appliedUrlRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (appliedUrlRef.current === destinationUrl) {
      return;
    }
    if (!ogHeadline && !ogDescription && !ogDisplayLink) {
      return;
    }

    appliedUrlRef.current = destinationUrl;

    /* Batch MobX mutations to avoid multiple re-renders */
    runInAction(() => {
      if (ogHeadline && !creative.get('headlines')?.length) {
        creative.set({ headlines: [ogHeadline] });
      }
      if (ogDescription && !creative.get('description')) {
        creative.set({ description: ogDescription });
      }
      if (ogDisplayLink && !creative.get('display_link')) {
        creative.set({ display_link: ogDisplayLink });
      }
    });
  }, [ogHeadline, ogDescription, ogDisplayLink, creative, destinationUrl]);

  function handleCallToAction({ id }: ICallToActionType): void {
    creative.set({ call_to_action_type: id });
  }

  /* Input onChange passes undefined for empty strings */
  function handleHeadlineChange(value?: string): void {
    creative.set({ headlines: [value ?? ''] });
  }

  return (
    <>
      <Textarea
        attribute={'primary_text'}
        helpText={t('Meta recommends 125 characters or less. Longer text may be truncated.')}
        label={t('Primary text')}
        maxLength={1000}
        model={creative}
        showCounter={true}
      />

      <Input
        helpText={t('Meta recommends 40 characters or less.')}
        label={t('Headline')}
        onChange={handleHeadlineChange}
        type={'text'}
        value={creative.get('headlines')?.[0] ?? ''}
      />

      <Input
        attribute={'display_link'}
        helpText={t(
          'Should go to the same domain as your destination URL. Must be 25 characters or less.',
        )}
        label={t('Display link')}
        maxLength={25}
        model={creative}
        showCounter={true}
        type={'text'}
      />

      <Input
        attribute={'description'}
        helpText={t('Meta recommends 27 characters or less.')}
        label={t('Description')}
        maxLength={100}
        model={creative}
        showCounter={true}
        type={'text'}
      />

      <UniversalSelect<ICallToActionType>
        helpText={t('Show a button or link that represents the action you want people to take')}
        label={t('Call to action')}
        menuPlacement={'top'}
        name={'call_to_action'}
        onSelectSingle={handleCallToAction}
        options={ctaOptions}
        placeholder={t('Select an option')}
        value={ctaOptions.find((option) => option.id === creative.get('call_to_action_type'))}
      />
    </>
  );
}

export default observer(MetaFields);
